import { EventEmitter, Injectable } from '@angular/core';
import { Route } from '@angular/router';
import { DTypeForm, DTypeFormOptions, DTypeFormSelectField, DTypeFormService } from '@grupo-constant/next-dtype/form';
import {
    DeleteDTypeData,
    DeleteDTypeDataReply,
    DType,
    DTypeCol,
    DTypeCollectionDN,
    DTypeFamilies,
    DTypeRepository,
    DTypeStatus,
    EmbeddedItem,
    EMBEDDED_COLLECTION_COLUMN_URI,
    EMBEDDED_ITEM_COLUMN_URI,
    FlushDType,
    FlushDTypeDataItem,
    FlushDTypeDataItemReply,
    FlushDTypeReply,
    JsonReply,
    NextLocator,
    Partaker,
    RetrieveDTypeByUri,
    RetrieveDTypeByUriReply,
    RetrieveDTypeCollectionDN,
    RetrieveDTypeCollectionDNReply,
    RetrieveDTypeRepositoryByUri,
    RetrieveDTypeRepositoryByUriReply,
    RetrieveMasterCollectionDN, SearchDTypeDataCollection, TABLEWITHFIXEDROWS_URI,
    TABLEWITHVARIABLEROWS_URI
} from '@grupo-constant/next-dtype/types';
import { LangDefinition, TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, of, switchMap, throwError } from 'rxjs';
import { NextMethodsEnum } from '../enums/next-methods.enum';
import { FormDesignComponent } from '../form/form-design/form-design.component';
import { FormSummaryComponent } from '../form/form-summary/form-summary.component';
import { HttpService } from '../http/http.service';
import { MASTER_DATA_URI } from '../master-data/master-data.service';
import { MasterData } from '../master-data/master-data.types';
import { MasterDataComponent } from '../master-data/master-data/master-data.component';
import { MasterDesignComponent } from '../master-data/master-design/master-design.component';
import { MasterSummaryComponent } from '../master-data/master-summary/master-summary.component';
import { TaskTypeComponent } from '../task-type/task-type/task-type.component';
import { DTypeFacadeListComponent } from './dtype-facade-list/dtype-facade-list.component';
import { DTypeMigrationComponent } from './dtype-migration/dtype-migration.component';
import { RetrieveDTypeDataCollectionBody, RetrieveDTypeDataCollectionReply, ChangeDTypeRepositoryNameBody, QuarantineRequestToGoLive, DTypeMigrationItem, DTypeToGoLiveScheduleBody, FlushDTypeRepositoryReply, FlushMasterFormHierarchyBody, FlushMasterFormHierarchyReply, ForwardDTypeToLiveBody, ImportDTypeMigrationItemBody, ResetDTypeBody, RetrieveMasterFormDestinyReply, RetrieveMasterFormHierarchyReply, ToggleDTypeLockBody, DownloadGoalModelBody, UploadGoalExcelBody, SearchCompanyUserMissionUserListDNRequest, SearchCompanyUserMissionUserListDNReply, RetrieveCompanyUserMissionUserListReply, FlushMasterOrderRequest, ToggleDTypeHiddenBody } from './dtype.types';
import { EditObjectGuard } from '../../modules/admin/projects/confirm-changes.guard';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { USER_URI } from '../../modules/admin/projects/projects.service';
import { RequestGoLiveBody } from '../../dialogs/request-go-live-dialog/request-go-live-dialog.types';
export const dtypesRoutes: Route[] = [
    {
        path: 'master',
        component: DTypeFacadeListComponent
    },
    {
        path: 'master/new',
        component: MasterDesignComponent,
        canDeactivate: [EditObjectGuard],
    },
    {
        path: 'master/design',
        component: MasterDesignComponent,
        canDeactivate: [EditObjectGuard],
    },
    {
        path: 'master/data',
        component: MasterDataComponent,
    },
    {
        path: 'master/summary',
        component: MasterSummaryComponent,
    },
    // {
    //     path: 'complex',
    //     component: DTypeFacadeListComponent,
    // },
    // {
    //     path: 'complex/new',
    //     component: ComplexDesignComponent,
    //     canDeactivate: [EditObjectGuard],
    // },
    // {
    //     path: 'complex/design',
    //     component: ComplexDesignComponent,
    //     canDeactivate: [EditObjectGuard],
    // },
    // {
    //     path: 'complex/summary',
    //     component: ComplexSummaryComponent,
    // },
    {
        path: 'form',
        component: DTypeFacadeListComponent
    },
    {
        path: 'form/new',
        component: FormDesignComponent,
        canDeactivate: [EditObjectGuard],
    },
    {
        path: 'form/design',
        component: FormDesignComponent,
        canDeactivate: [EditObjectGuard],
    },
    {
        path: 'form/summary',
        component: FormSummaryComponent,
    },
    {
        path: 'tasktype',
        component: DTypeFacadeListComponent
    },
    {
        path: 'tasktype/new',
        component: TaskTypeComponent,
        canDeactivate: [EditObjectGuard],
    },
    {
        path: 'tasktype/:tasktypeid',
        component: TaskTypeComponent,
        canDeactivate: [EditObjectGuard],
    },
    {
        path: 'masterSystem',
        component: DTypeFacadeListComponent
    },
    {
        path: 'masterSystem/new',
        component: MasterDesignComponent,
        canDeactivate: [EditObjectGuard],
    },
    {
        path: 'masterSystem/data',
        component: MasterDataComponent,
    },
    {
        path: 'dtypemigration',
        component: DTypeMigrationComponent
    },
];

@Injectable({
    providedIn: 'root'
})
export class DTypeService {

    dtypeFamilies: DTypeFamilies;

    columns$ = new BehaviorSubject<DTypeCol[]>([]);
    tableComponentColumns$ = new BehaviorSubject<DTypeCol[]>([]);
    dtype = new BehaviorSubject<DType>(undefined);
    updateDTypeSidebar = new EventEmitter<void>();

    private dtypeCache: Map<string, Map<string, DType>> = new Map();
    private dtypeRepoCache: Map<string, Map<string, DTypeRepository>> = new Map();

    constructor(
        private fuseConfirmationService: FuseConfirmationService,
        private httpService: HttpService,
        private translocoService: TranslocoService,
        private dTypeFormService: DTypeFormService
    ) { }

    get dtype$(): Observable<DType> {
        return this.dtype.asObservable();
    }

    setDtype(dtype: DType) {
        this.dtype.next(dtype);
    }

    async getDatatype(args: { retrieveDTypeBody: RetrieveDTypeByUri; includeEmbedded?: boolean }) {
        if (!args?.retrieveDTypeBody) {
            return null;
        }
        const dType = await firstValueFrom(this.retrieveDtypeByUri(args.retrieveDTypeBody));
        if (!dType) {
            return null;
        }

        if (args.includeEmbedded) {
            for (const col of dType.column) {
                if (col.type === EMBEDDED_ITEM_COLUMN_URI || col.type === EMBEDDED_COLLECTION_COLUMN_URI ||
                    col.type === TABLEWITHFIXEDROWS_URI || col.type === TABLEWITHVARIABLEROWS_URI) {
                    (col as any).dType = await this.getDatatype({
                        retrieveDTypeBody: { ...args.retrieveDTypeBody, uri: (col as EmbeddedItem).uri, bindableOptions: true },
                        includeEmbedded: true
                    });
                }
            }
        }

        this.setDtype(dType);
        return dType;
    }

    retrieveDTypeRepositoryByUri(body: RetrieveDTypeRepositoryByUri): Observable<DTypeRepository> {
        // if (this.dtypeRepoCache.has(body.uri) && this.dtypeRepoCache.get(body.uri).has(body.status)) {
        //     return of(this.dtypeRepoCache.get(body.uri).get(body.status));
        // }
        return this.httpService.post<RetrieveDTypeRepositoryByUriReply>(NextMethodsEnum.retrieveDTypeRepositoryByUri, body)
            .pipe(
                switchMap(async (reply) => {
                    // for (const key of Object.keys(reply.collection)) {
                    //     if (!this.dtypeRepoCache.has(body.uri)) {
                    //         this.dtypeRepoCache.set(key, new Map<string, DTypeRepository>());
                    //     }
                    //     if (!this.dtypeCache.has(body.uri)) {
                    //         this.dtypeCache.set(key, new Map<string, DType>());
                    //     }
                    //     this.dtypeRepoCache.get(key).set(body.status, reply.collection[key]);
                    // }
                    const repository = reply?.collection[body.uri];
                    if (repository?.facadeUri === MASTER_DATA_URI) {
                        const master = repository.payload as MasterData;
                        if (master.allowedUsers) {
                            master.allowedUsersProfile = master.allowedUsers.map(user => {
                                return reply.partaker?.[USER_URI]?.[user] ?? user;
                            });
                        }
                    }
                    const dtype = repository?.payload;
                    if (dtype) {
                        for (const col of dtype.column) {
                            if (col.type === EMBEDDED_ITEM_COLUMN_URI || col.type === EMBEDDED_COLLECTION_COLUMN_URI || col.type === TABLEWITHFIXEDROWS_URI ||
                                col.type === TABLEWITHVARIABLEROWS_URI) {
                                (col as any).dType = await this.getDatatype({
                                    retrieveDTypeBody: { ...body, uri: (col as EmbeddedItem).uri },
                                    includeEmbedded: true
                                });
                            }
                        }
                    }
                    return repository;
                })
            );
    }

    retrieveDTypeDataCollection(body: RetrieveDTypeDataCollectionBody) {
        return this.httpService.post<RetrieveDTypeDataCollectionReply>(NextMethodsEnum.retrieveDTypeDataCollection, body);
    }

    retrieveDTypeDataCollectionByUri(uri: string): Observable<any[]> {
        return this.retrieveDTypeDataCollection({ uri, count: false, columnsForList: false })
            .pipe(map(response => response.data));
    }

    retrieveDtypeDataItemByUriAndOid(uri: string, oid: string) {
        return this.httpService.post<any>(NextMethodsEnum.retrieveDTypeDataItem, { uri, oid })
            .pipe(map(response => response.data));
    }

    retrieveDTypeBuilderCollection(): Observable<DTypeFamilies> {
        if (!this.dtypeFamilies) {
            return this.httpService.post<any>(NextMethodsEnum.retrieveDTypeBuilderCollection, {})
                .pipe(
                    map((response) => {
                        if (!!response) {
                            this.dtypeFamilies = response.collection;
                            return this.dtypeFamilies;
                        }
                        return {};
                    })
                );
        } else {
            return of(this.dtypeFamilies);
        }
    }

    /**
     * Devuelve el conjunto de dtypes (datos maestros, formularios, catálogos...) para
     * pintar los menús laterales en las pantallas de marca, cliente...
     */
    retrieveNextDtypeCollection(body: RetrieveDTypeCollectionDN): Observable<[DTypeCollectionDN, Partaker]> {
        return this.httpService.post<RetrieveDTypeCollectionDNReply>(NextMethodsEnum.retrieveDTypeCollectionDN, body)
            .pipe(map(reply => [(reply?.collection ?? {}) as DTypeCollectionDN, reply?.partaker ?? {}]));
    }

    /**
     * Devuelve el conjunto de maestros para
     * pintar los menús laterales en las pantallas de marca, cliente...
     */
    retrieveNextMasterSystemCollection(body: RetrieveMasterCollectionDN): Observable<[DTypeCollectionDN, Partaker]> {
        return this.httpService.post<RetrieveDTypeCollectionDNReply>(NextMethodsEnum.retrieveMasterCollectionDN, body)
            .pipe(map(reply => [(reply?.collection ?? {}) as DTypeCollectionDN, reply?.partaker ?? {}]));
    }

    retrieveDTypeHierarchy(locator: NextLocator) {
        return this.httpService.post<RetrieveMasterFormHierarchyReply>(NextMethodsEnum.retrieveMasterFormHierarchy, { locator })
            .pipe(map(reply => reply?.data ?? []));
    }

    flushDTypeHierarchy(body: FlushMasterFormHierarchyBody) {
        return this.httpService.post<FlushMasterFormHierarchyReply>(NextMethodsEnum.flushMasterFormHierarchyItem, body);
    }

    exportDTypeHierarchy(items: DTypeMigrationItem[]): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.exportMasterFormHierarchyItem, {hierarchyItems: items}, 'blob');
    }

    importDTypeHierarchy(body: ImportDTypeMigrationItemBody): Observable<RetrieveMasterFormHierarchyReply> {
        return this.httpService.post<RetrieveMasterFormHierarchyReply>(NextMethodsEnum.importMasterFormHierarchyItem, body);
    }

    retrieveDTypeDestiny(locator: NextLocator) {
        return this.httpService.post<RetrieveMasterFormDestinyReply>(NextMethodsEnum.retrieveNodeHierarchyItem, { locator })
            .pipe(map(reply => reply?.data ?? []));
    }

    retrieveDTypeGoLiveRequest(uri: string) {
        return this.httpService.post<QuarantineRequestToGoLive>(NextMethodsEnum.retrieveDTypeGoLiveRequest, { uri });
    }

    /**
     * Elimina el elemento con la uri proporcionada
     *
     * @param uri
     */
    deleteNextDType(uri: string) {
        const dialogRef = this.fuseConfirmationService.open({
            title: this.translocoService.translate('warning'),
            message: this.translocoService.translate('deleteDTypeConfirm'),
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('cancel')
                }
            }
        });

        return dialogRef.afterClosed()
            .pipe(
                switchMap((result) => {
                    if (result === 'confirmed') {
                        if (uri.startsWith('taskType')) {
                            return this.httpService.post<JsonReply>(NextMethodsEnum.deleteTaskType, { uri })
                            .pipe(
                                catchError((error) => {
                                    console.error(error);
                                    return throwError(() => error);
                                })
                            );
                        } else {
                            return this.httpService.post<JsonReply>(NextMethodsEnum.deleteDType, { uri })
                            .pipe(
                                catchError((error) => {
                                    console.error(error);
                                    return throwError(() => error);
                                })
                            );
                        }
                    }
                })
            );
    }

    /**
     * Devuelve el modelo del formulario de un datatype
     */
    async getDtypeFormByUri(retrieveDTypeBody: RetrieveDTypeByUri, values?: any, options: { readonly?: boolean; isUpdate?: boolean } = {}): Promise<DTypeForm> {
        let formModel: DTypeForm;
        const formDatatype = await this.getDatatype({ retrieveDTypeBody, includeEmbedded: true });
        if (formDatatype) {
            const formOptions: DTypeFormOptions = {
                readonly: options?.readonly,
                isUpdate: options?.isUpdate,
                locale: this.translocoService.getActiveLang(),
                availableLangs: this.translocoService.getAvailableLangs() as string[]
            };
            formModel = this.dTypeFormService.buildForm(formDatatype, values, formOptions) as DTypeForm;
        }
        return formModel;
    }

    getDtypeFormFromDtype(dtype: DType, values?: any, options: { readonly?: boolean; isUpdate?: boolean } = {}) {
        const formOptions: DTypeFormOptions = {
            readonly: options?.readonly,
            isUpdate: options?.isUpdate,
            locale: this.translocoService.getActiveLang(),
            availableLangs: this.translocoService.getAvailableLangs() as string[]
        };
        return this.dTypeFormService.buildForm(dtype, values, formOptions) as DTypeForm;
    }

    getExternalFilterDtypeFormFromDtype(masterData: MasterData): DTypeForm {
        if (!masterData?.columnsForExternalFilter || masterData.columnsForExternalFilter.length == 0)
            return null;
        
        const filteredColumns = masterData.column
            .filter(col => masterData.columnsForExternalFilter.includes(col.name))
            .map(col => ({
                ...col,
                mandatory: false,
                readonly: false,
                readonlyOnUpdate: false
            }));
        const externalFilterDType: DType = {
            ...masterData,
            column: filteredColumns
        };

        const form: DTypeForm = this.getDtypeFormFromDtype(externalFilterDType); 
        Object.keys(form.fields)
            .forEach(key => {
            const field = form.fields[key];

            if (field?.browserOpts) {
                field.browserOpts.newLine = false;
                field.browserOpts.alignment = 'LEFT';
                field.browserOpts.inLineWithCaption = false;
                field.browserOpts.forbiddenOptions = [];

                if (field instanceof DTypeFormSelectField) {
                    field.browserOpts.width = 'FOURTH';
                }
            }
        });

        form.control.reset();
        
        return form;
    }

    flushDType(body: FlushDType) {
        return this.httpService.post<FlushDTypeRepositoryReply>(NextMethodsEnum.flushDTypeRepository, body);
    }

    forwardDTypeToLive(body: ForwardDTypeToLiveBody) {
        return this.httpService.post<FlushDTypeReply>(NextMethodsEnum.flushDTypeRepositoryToGoLiveRequest, body);
    }

    flushDTypeToGoLiveRequest(body: RequestGoLiveBody) {
        return this.httpService.post<FlushDTypeReply>(NextMethodsEnum.flushDTypeRepositoryToGoLiveRequest, body);
    }

    flushDTypeToGoLiveSchedule(body: DTypeToGoLiveScheduleBody) {
        return this.httpService.post<FlushDTypeReply>(NextMethodsEnum.flushDTypeRepositoryToGoLiveSchedule, body);
    }

    updateDType(uri: string, repo: DTypeRepository, goLive?: boolean) {
        if (!this.dtypeRepoCache.get(uri)) {
            this.dtypeRepoCache.set(uri, new Map<string, DTypeRepository>());
        }
        if (!this.dtypeCache.get(uri)) {
            this.dtypeCache.set(uri, new Map<string, DType>());
        }
        if (goLive) {
            this.dtypeRepoCache.get(uri).set(DTypeStatus.LIVE, repo);
            this.dtypeCache.get(uri).set(DTypeStatus.LIVE, (repo.payload as DType));
        }
        this.dtypeRepoCache.get(uri).set(DTypeStatus.DESIGN, repo);
        this.dtypeCache.get(uri).set(DTypeStatus.DESIGN, (repo.payload as DType));
    }

    discardDesign(body: ResetDTypeBody) {
        return this.httpService.post<FlushDTypeReply>(NextMethodsEnum.resetDTypeRepository, body);
    }

    changeDTypeRepositoryName(body: ChangeDTypeRepositoryNameBody) {
        return this.httpService.post<FlushDTypeReply>(NextMethodsEnum.changeDTypeRepositoryName, body);
    }

    emitUpdateSidebarEvent() {
        this.updateDTypeSidebar.emit();
    }

    flushDTypeDataItem(body: FlushDTypeDataItem) {
        return this.httpService.post<FlushDTypeDataItemReply>(NextMethodsEnum.flushDTypeDataItem, body)
            .pipe(map(reply => reply?.data));
    }

    flushMasterOrder(request: FlushMasterOrderRequest) {
        return this.httpService.post<JsonReply>(NextMethodsEnum.flushMasterOrder, request);
    }

    searchDTypeData(uri: string, expr: string, blind = false) {
        return this.httpService.post<RetrieveDTypeDataCollectionReply>(NextMethodsEnum.searchDTypeDataCollection, { uri, expr, blind })
            .pipe(map(reply => reply?.data ? reply.data : []));
    }

    deleteDTypeData(body: DeleteDTypeData) {
        return this.httpService.post<DeleteDTypeDataReply>(NextMethodsEnum.deleteDTypeData, body);
    }

    searchDTypeDataCollection(body: SearchDTypeDataCollection) {
        return this.httpService.post<RetrieveDTypeDataCollectionReply>(NextMethodsEnum.searchDTypeDataCollection, body);
    }

    getLocatorFromString(stringLocator: string): NextLocator {
        const locator: NextLocator = {};
        const splitLocator = stringLocator.split(/[/,]/ig);
        let internalSplit: string[];
        for (const split of splitLocator) {
            internalSplit = split.split('=');
            if (internalSplit.length > 1) {
                switch (internalSplit[0]) {
                    case 'OU':
                        locator.OU = internalSplit[1];
                        break;
                    case 'Brand':
                        locator.marca_code = internalSplit[1];
                        break;
                    case 'Customer':
                        locator.cliente_code = Number(internalSplit[1]);
                        break;
                    case 'Project':
                        locator.projectId = internalSplit[1];
                        break;
                    case 'Service':
                        locator.serviceId = internalSplit[1];
                        break;
                }
            }
        }
        return locator;
    }

    toggleDTypeLock(body: ToggleDTypeLockBody) {
        return this.httpService.post<FlushDTypeReply>(NextMethodsEnum.toggleDTypeRepositoryLock, body);
    }

    toggleDTypeHidden(body: ToggleDTypeHiddenBody) {
        return this.httpService.post<FlushDTypeReply>(NextMethodsEnum.toggleDTypeRepositoryHidden, body);
    }

    downloadGoalModel(body: DownloadGoalModelBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadServiceGoalsModelXSSF, body, 'blob');
    }

    uploadExcel(body: UploadGoalExcelBody): Observable<JsonReply> {
        return this.httpService.post<JsonReply>(NextMethodsEnum.flushDTypeExcelUpload, body);
    }

    searchCompanyUserMissionUserListDN(request: SearchCompanyUserMissionUserListDNRequest) {
        return this.httpService.post<SearchCompanyUserMissionUserListDNReply>(NextMethodsEnum.searchCompanyUserMissionUserListDN, request)
            .pipe(map(reply => reply.companyUser ?? []));
    }

    retrieveCompanyUserMissionUserList(userIds: string) {
        return this.httpService.post<RetrieveCompanyUserMissionUserListReply>(NextMethodsEnum.retrieveCompanyUserMissionUserList, { userIds })
            .pipe(map(reply => reply.companyUser ?? []));
    }

    private retrieveDtypeByUri(body: RetrieveDTypeByUri): Observable<DType> {
        return this.httpService.post<RetrieveDTypeByUriReply>(NextMethodsEnum.retrieveDTypeByUri, body)
            .pipe(
                map(reply => reply?.collection?.[body.uri])
            );
    }

}
